import React from 'react';
import { IntlShape } from 'react-intl';
import { TableCell, TableHeader } from '../../../components/Table/Table';

import { addModifiers, changeSmallNumberFormat } from '../../../shared/helpers';
import { FilterOrder } from '../Filters/auxiliaryFields';
import { DataTableColumn } from './DataTableCommonTypes';
import { DatasetField } from '../../../shared/api/api';
import { ColumnDef } from '@tanstack/table-core/src/types';
import { AccessorFn, CellContext, createColumnHelper } from '@tanstack/react-table';
import { formatMessageHelper } from '../../../shared/helpers/internationalization';

export function renderPercentValue(value?: number) {
  if (!value) {
    return '—';
  }
  return changeSmallNumberFormat({ value, type: 'percent', toFixedValue: 0 });
}

export function renderTurnoverCell(original: DatasetField) {
  return getRegionValueContent(original, 'turnover');
}

function getRegionValueContent(original: DatasetField, key: 'turnover' | 'coverage' | 'dateCoverage') {
  return <>{renderPercentValue(original[key])}</>;
}

function getRowOriginal<DataType>(cellContext: CellContext<DataType, DataType>) {
  // @ts-ignore
  if (cellContext.aggregated) {
    // @ts-ignore
    return cellContext.subRows[0]._original;
  }
  return cellContext.row.original;
}

export function renderCoverageCell<DataType>(props: CellContext<DataType, DataType>) {
  const original = getRowOriginal(props);
  return <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{getRegionValueContent(original, 'coverage')}</div>;
}

export function renderDateCoverageCell<DataType>(props: CellContext<DataType, DataType>) {
  const original = getRowOriginal(props);
  return (
    <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{getRegionValueContent(original, 'dateCoverage')}</div>
  );
}

export interface ColumnTransform {
  intl: IntlShape;
  sortOrder: FilterOrder;
  handleOrderChange: (id: string) => void;
  isOrderDisabled?: boolean;
}

export function getColumnTransform<Data>({
  intl,
  sortOrder,
  handleOrderChange,
  isOrderDisabled,
}: ColumnTransform): (column: DataTableColumn<Data>) => ColumnDef<Data> {
  function transformColumn(column: DataTableColumn<Data>): ColumnDef<Data> {
    const Cell = column.cell ? column.cell : TableCell(null);
    const isHeaderLocalized = column?.isHeaderLocalized ?? true;

    return {
      ...column,
      ...(isOrderDisabled && { enableSorting: false }),
      header: TableHeader({
        name: getName(isHeaderLocalized, intl, column),
        sort:
          column.columns || (typeof column.enableSorting === 'boolean' && !column.enableSorting) || isOrderDisabled ? null : sortOrder.key,
        enableSorting: !isOrderDisabled,
        order: sortOrder.isAscending,
        onClick: handleOrderChange,
        isLeft: true,
        className: column.alignHeader === 'right' ? 'header-right' : '',
      }),
      columns: column.columns?.map(column => transformSubColumn({ column, isOrderDisabled })) ?? [
        {
          id: column.id,
          // @ts-ignore
          accessor: column.accessor,
          accessorFn: column.accessorFn,
          filterFn: column.filterFn,
          minWidth: column.size ? column.size : 120,
          cell: Cell,
          display: column.display,
          sortable: false,
          size: column.size,
          aggregate: (values: any, rows: any) => {
            if (column.id === 'coverage') {
              return values[0]?.coverage ?? rows[0]._original.coverage;
            }
            if (column.id === 'dateCoverage') {
              return values[0]?.dateCoverage ?? rows[0]._original.dateCoverage;
            }

            return values[0];
          },
          Aggregated: (rowInfo: any) => {
            if (rowInfo.subRows?.length === 1) {
              return Cell(rowInfo);
            }

            return column.aggregated?.(rowInfo);
          },
        },
      ],
    };
  }

  function transformSubColumn({
    column,
    isOrderDisabled,
  }: {
    column: DataTableColumn<Data>;
    isOrderDisabled?: boolean;
  }): ColumnDef<Data, Data> {
    const columnHelper = createColumnHelper<Data>();
    const isHeaderLocalized = column?.isHeaderLocalized ?? true;
    const Cell = column.cell ? column.cell : TableCell(null);
    if (column.columns) {
      // @ts-ignore
      return columnHelper.group({
        header: TableHeader({
          name: getName(isHeaderLocalized, intl, column),
          sort: isOrderDisabled ? null : sortOrder.key,
          order: sortOrder.isAscending,
          onClick: handleOrderChange,
        }),
        id: column.id,
        size: column.size ? column.size : 120,
        //@ts-ignore
        cell: Cell,
        //@ts-ignore
        Aggregated: (rowInfo: any) => {
          if (rowInfo.subRows?.length === 1) {
            return Cell(rowInfo);
          }

          return null;
        },
        aggregate: (values: any, rows: any) => {
          if (column.id === 'coverage') {
            return values[0].coverage;
          }
          if (column.id === 'dateCoverage') {
            return values[0]?.dateCoverage ?? rows[0]._original.dateCoverage;
          }
          return values[0];
        },
      });
    }

    return columnHelper.accessor(column.accessor as AccessorFn<Data>, {
      header: TableHeader({
        name: getName(isHeaderLocalized, intl, column),
        sort: isOrderDisabled ? null : sortOrder.key,
        order: sortOrder.isAscending,
        onClick: handleOrderChange,
      }),
      id: column.id,
      size: column.size ? column.size : 120,
      cell: Cell,
      //@ts-ignore
      Aggregated: (rowInfo: any) => {
        if (rowInfo.subRows?.length === 1) {
          return Cell(rowInfo);
        }

        return null;
      },
      aggregate: (values: any, rows: any) => {
        if (column.id === 'coverage') {
          return values[0].coverage;
        }
        if (column.id === 'dateCoverage') {
          return values[0]?.dateCoverage ?? rows[0]._original.dateCoverage;
        }
        return values[0];
      },
    });
  }

  return transformColumn;
}

function getName<Data>(isHeaderLocalized: boolean, intl: IntlShape, column: DataTableColumn<Data>) {
  return isHeaderLocalized ? formatMessageHelper(intl, column.name) : column.name;
}
